@if (isAuthenticated) {
  <div>
    <!-- Off-canvas menu for mobile, show/hide based on off-canvas menu state. -->
    <div class="fixed inset-0 z-[60] lg:hidden" role="dialog" aria-modal="true" [class.hidden]="!showMobileMenu">
      <!--
        Off-canvas menu backdrop, show/hide based on off-canvas menu state.

        Entering: "transition-opacity ease-linear duration-300"
          From: "opacity-0"
          To: "opacity-100"
        Leaving: "transition-opacity ease-linear duration-300"
          From: "opacity-100"
          To: "opacity-0"
      -->
      <div class="fixed inset-0 bg-gray-900/80" aria-hidden="true" (click)="closeMobileMenu()"></div>

      <div class="fixed inset-0 flex">
        <!--
          Off-canvas menu, show/hide based on off-canvas menu state.

          Entering: "transition ease-in-out duration-300 transform"
            From: "-translate-x-full"
            To: "translate-x-0"
          Leaving: "transition ease-in-out duration-300 transform"
            From: "translate-x-0"
            To: "-translate-x-full"
        -->
        <!-- Close button positioned at the very top of the screen -->
        <div class="absolute top-4 right-4 z-[70]">
          <button type="button" class="bg-red-500 hover:bg-red-600 text-white rounded-full p-3 shadow-lg" (click)="closeMobileMenu()">
            <span class="sr-only">Close sidebar</span>
            <svg class="size-6" fill="none" viewBox="0 0 24 24" stroke-width="2" stroke="currentColor" aria-hidden="true">
              <path stroke-linecap="round" stroke-linejoin="round" d="M6 18 18 6M6 6l12 12" />
            </svg>
          </button>
        </div>
        
        <div class="relative mr-16 flex w-full max-w-xs flex-1">
          <!-- Mobile sidebar using app-navigation component -->
          <div class="flex grow flex-col bg-cdoclightblue" (click)="$event.stopPropagation()">
            <app-navigation></app-navigation>
          </div>
        </div>
      </div>
    </div>

    <!-- Static sidebar for desktop -->
    <div class="hidden lg:fixed lg:inset-y-0 lg:z-50 lg:flex lg:w-72 lg:flex-col transition-transform duration-300 ease-in-out" [class.-translate-x-full]="toggleNav">
      <!-- Desktop sidebar using app-navigation component -->
      <div class="flex grow flex-col bg-cdoclightblue relative">
        <app-navigation></app-navigation>
        <!-- Toggle button inside sidebar (only visible when sidebar is visible) -->
        @if (!toggleNav) {
          <button 
            type="button" 
            class="absolute top-3 right-3 z-50 bg-white hover:bg-gray-100 text-gray-700 rounded-full p-1.5 shadow-md border border-gray-300 transition-all hover:scale-110"
            (click)="toggleNavigation()"
            [attr.title]="'HIDE_NAVIGATION' | translate"
            aria-label="{{ 'HIDE_NAVIGATION' | translate }}">
            <svg class="size-4" fill="none" viewBox="0 0 24 24" stroke-width="2" stroke="currentColor" aria-hidden="true">
              <path stroke-linecap="round" stroke-linejoin="round" d="M18.75 19.5l-7.5-7.5 7.5-7.5m-6 15l-7.5-7.5 7.5-7.5" />
            </svg>
          </button>
        }
      </div>
    </div>

    <!-- Floating button to show navigation when hidden (desktop only) -->
    @if (toggleNav && currentUser()?.is_guest === 0) {
      <button 
        type="button" 
        class="hidden lg:block lg:fixed lg:left-2 lg:top-1/2 lg:-translate-y-1/2 lg:z-50 bg-cdocblue hover:bg-cdocblue/90 text-white rounded-r-md p-2 shadow-md border border-gray-300 transition-all duration-300 hover:scale-105"
        (click)="toggleNavigation()"
        [attr.title]="'SHOW_NAVIGATION' | translate"
        aria-label="{{ 'SHOW_NAVIGATION' | translate }}">
        <svg class="size-4" fill="none" viewBox="0 0 24 24" stroke-width="2" stroke="currentColor" aria-hidden="true">
          <path stroke-linecap="round" stroke-linejoin="round" d="M11.25 4.5l7.5 7.5-7.5 7.5m-6-15l7.5 7.5-7.5 7.5" />
        </svg>
      </button>
    }

    <!-- Mobile headers -->
    <div class="lg:hidden fixed top-0 left-0 right-0 z-50 bg-white">
      <!-- Top header -->
      <div class="flex h-14 shrink-0 items-center border-b border-gray-300 bg-gradient-to-r from-blue-100 to-purple-100 px-4 shadow-xs sm:px-6">
        <!-- Mobile menu button -->
        <button type="button" class="-m-2.5 p-2.5 text-gray-700" (click)="openMobileMenu()">
          <span class="sr-only">Open sidebar</span>
          <svg class="size-6" fill="none" viewBox="0 0 24 24" stroke-width="1.5" stroke="currentColor" aria-hidden="true" data-slot="icon">
            <path stroke-linecap="round" stroke-linejoin="round" d="M3.75 6.75h16.5M3.75 12h16.5m-16.5 5.25h16.5" />
          </svg>
        </button>

        <!-- Top header - takes remaining space -->
        <div class="flex-1">
          <app-top-header></app-top-header>
        </div>
      </div>

      <!-- Main header -->
      <div class="flex min-h-14 shrink-0 items-center border-b border-gray-300 bg-white px-4 shadow-xs sm:px-6">
        <div class="flex-1">
          <app-header (headerHeightChanged)="recalculatePadding()"></app-header>
        </div>
      </div>
    </div>

    <!-- Desktop headers -->
    <div class="hidden lg:block fixed top-0 right-0 z-50 bg-white transition-all duration-300" [class.left-72]="!toggleNav" [class.left-0]="toggleNav">
      <!-- Top header -->
      <div class="flex h-14 shrink-0 items-center border-b border-gray-300 bg-gradient-to-r from-blue-100 to-purple-100 px-4 shadow-xs sm:px-6 lg:px-8">
        <!-- Mobile menu button -->
        <button type="button" class="-m-2.5 p-2.5 text-gray-700 lg:hidden" (click)="openMobileMenu()">
          <span class="sr-only">Open sidebar</span>
          <svg class="size-6" fill="none" viewBox="0 0 24 24" stroke-width="1.5" stroke="currentColor" aria-hidden="true" data-slot="icon">
            <path stroke-linecap="round" stroke-linejoin="round" d="M3.75 6.75h16.5M3.75 12h16.5m-16.5 5.25h16.5" />
          </svg>
        </button>

        <!-- Top header - takes remaining space -->
        <div class="flex-1">
          <app-top-header></app-top-header>
        </div>
      </div>

      <!-- Main header -->
      <div class="flex min-h-14 shrink-0 items-center border-b border-gray-300 bg-white px-4 shadow-xs sm:px-6 lg:px-8">
        <div class="flex-1">
          <app-header (headerHeightChanged)="recalculatePadding()"></app-header>
        </div>
      </div>
    </div>

    <!-- Single main content area for both mobile and desktop -->
    <main class="transition-all duration-300" [class.lg:pl-72]="!toggleNav" [class.lg:pl-0]="toggleNav" style="padding-top: calc(112px + env(safe-area-inset-top));">
      <div class="px-4 sm:px-6 lg:px-8">
        <!-- Your content -->
        <router-outlet></router-outlet>
      </div>
    </main>
  </div>
}

@if (!isAuthenticated) {
  <router-outlet></router-outlet>
} 